import { useQuery } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  ApprovalMetricsDto,
  ConversionMetricsDto,
  GoogleAnalyticsReportDto,
  GoogleAnalyticsReportType,
  OrdersTodayDto,
  StatisticsOverviewDto,
  TopVendorDto,
  TopVendorMetric,
} from "../models/responses/StatisticsResponses";

export interface StatisticsRepository {
  useOverview(from: string, to: string): Resource<StatisticsOverviewDto>;
  useTopVendors(
    from: string,
    to: string,
    metric: TopVendorMetric,
    limit: number,
  ): Resource<TopVendorDto[]>;
  useConversion(from: string, to: string): Resource<ConversionMetricsDto>;
  useApproval(from: string, to: string): Resource<ApprovalMetricsDto>;
  useGoogleAnalytics(
    from: string,
    to: string,
    report: GoogleAnalyticsReportType,
  ): Resource<GoogleAnalyticsReportDto>;
  useOrdersToday(date: string): Resource<OrdersTodayDto>;
}

export function useStatisticsRepository(): StatisticsRepository {
  const apiClient = useApiClient();

  // Stale time of 60 seconds — these aren't real-time numbers and we don't
  // want a re-render storm if multiple widgets share the same date range.
  const config = { staleTime: 60_000 };

  function useOverview(
    from: string,
    to: string,
  ): Resource<StatisticsOverviewDto> {
    return useQuery(
      ["statistics", "overview", from, to],
      () => apiClient.getStatisticsOverview(from, to),
      config,
    );
  }

  function useTopVendors(
    from: string,
    to: string,
    metric: TopVendorMetric,
    limit: number,
  ): Resource<TopVendorDto[]> {
    return useQuery(
      ["statistics", "top-vendors", from, to, metric, limit],
      () => apiClient.getStatisticsTopVendors(from, to, metric, limit),
      config,
    );
  }

  function useConversion(
    from: string,
    to: string,
  ): Resource<ConversionMetricsDto> {
    return useQuery(
      ["statistics", "conversion", from, to],
      () => apiClient.getStatisticsConversion(from, to),
      config,
    );
  }

  function useApproval(
    from: string,
    to: string,
  ): Resource<ApprovalMetricsDto> {
    return useQuery(
      ["statistics", "approval", from, to],
      () => apiClient.getStatisticsApproval(from, to),
      config,
    );
  }

  function useGoogleAnalytics(
    from: string,
    to: string,
    report: GoogleAnalyticsReportType,
  ): Resource<GoogleAnalyticsReportDto> {
    return useQuery(
      ["statistics", "google-analytics", from, to, report],
      () => apiClient.getStatisticsGoogleAnalytics(from, to, report),
      config,
    );
  }

  function useOrdersToday(date: string): Resource<OrdersTodayDto> {
    return useQuery(
      ["statistics", "orders-today", date],
      () => apiClient.getStatisticsOrdersToday(date),
      {
        // Monitoring surface for the current day — always fresh on entry,
        // and keep the previous day's numbers visible while switching dates.
        staleTime: 0,
        keepPreviousData: true,
      },
    );
  }

  return {
    useOverview,
    useTopVendors,
    useConversion,
    useApproval,
    useGoogleAnalytics,
    useOrdersToday,
  };
}
